import { engineComponent, EngineComponentFactory, Logger } from "neo-common";
import { Communication } from "neo-games";
import { ComponentTypes } from "../../Components/ComponentTypes";
import { GambleActions } from "../Common/Actions/GambleActions";
import { DataManager } from "../../Managers/DataManager";
import { GameManager } from "../../Managers/GameManager";
import { GambleUserControls } from "../Common/UserControls/GambleUserControls";
import { ActionsTypes, ActionSet } from "../Common/Actions/ActionSet";
import { TerminalActions } from "../Common/Actions/TerminalActions";
import { AppContext } from "../../../Common/AppContext";
import { StyxClientHostEvents } from "./FireBirdTypes";
import { ModuleManager } from "../../Managers/ModuleManager";
import { GameData } from "../Common/Datas/GameData";
import { RemoteManager } from "../../Managers/RemoteManager";

@engineComponent(ComponentTypes.HtmlElement)
export class FireBirdGambleUserControls extends GambleUserControls {
    protected ledStripe: string;

    protected _terminalActions: ActionSet;

    constructor() {
        super();
    }

    create(node: Element): void {
        this._terminalActions = new ActionSet(
            `${this._key}.terminalActions`,
            EngineComponentFactory.getComponentName(this),
        );

        super.create(node);

        this.ledStripe = node.hasAttribute("LedStripe") ? node.getAttribute("LedStripe") : "255;255;255";

        this.handleClientHostReload = this.handleClientHostReload.bind(this);

        // event 'reload' is fired when client host requests reload of actions (styx/firebird only)
        AppContext.clientHost.on(StyxClientHostEvents.Reload, this.handleClientHostReload);
    }

    private handleClientHostReload() {
        if (!ModuleManager.gambleActive()) {
            return;
        }

        if (DEBUG) {
            Logger.debugGroup("FireBirdGambleUserControls", `Event 'reload' arrived, actions updated...`);
        }

        AppContext.clientHost.setLEDStripe(this.ledStripe, 0);
        AppContext.clientHost.actionsUpdated(this.getAllTerminalActions());
    }

    protected get terminalActions(): ActionSet {
        return this._terminalActions;
    }

    public getAllTerminalActions(): ActionsTypes {
        return this._terminalActions.getAllActions();
    }

    registerActions(): void {
        super.registerActions();
        this.terminalActions.registerActions({
            [TerminalActions.Payout]: false,
            [TerminalActions.BackToLobby]: false,
            [TerminalActions.StartWithCollect]: false,
            [TerminalActions.ActionLeft]: false,
            [TerminalActions.ActionRight]: false,
            [TerminalActions.BetOrGamble]: false,
        });
    }

    dispatchAction(action: string): void {
        if (action === TerminalActions.StartWithCollect) {
            if (this.actions.getActionState(GambleActions.ActionGambleCollect)) {
                action = GambleActions.ActionGambleCollect;
            }
        }

        if (action === TerminalActions.BetOrGamble) {
            if (this.actions.getActionState(GambleActions.ActionGambleStart)) {
                action = GambleActions.ActionGambleStart;
            }
        }

        if (action === TerminalActions.ActionLeft) {
            if (this.actions.getActionState(GambleActions.ActionGambleRed)) {
                action = GambleActions.ActionGambleRed;
            }
        }

        if (action === TerminalActions.ActionRight) {
            if (this.actions.getActionState(GambleActions.ActionGambleBlack)) {
                action = GambleActions.ActionGambleBlack;
            }
        }

        super.dispatchAction(action);
    }

    prepareNewGameRound(): void {
        super.prepareNewGameRound();
        if (!DataManager.GameLimit) {
            let softLimit: boolean = false;
            if (GameManager.gambleResponse.colorWin === 0) {
                softLimit = true;
            }

            if (!softLimit) {
                RemoteManager.gamble("idle");
            }

            this.terminalActions.changeActionStates({
                [TerminalActions.StartWithCollect]: true,
                [TerminalActions.ActionLeft]: !softLimit,
                [TerminalActions.ActionRight]: !softLimit,
                [TerminalActions.BetOrGamble]: false,
            });
        }

        AppContext.clientHost.actionsUpdated(this.getAllTerminalActions());
    }

    initIntro(): void {
        AppContext.clientHost.setLEDStripe(this.ledStripe, 2);

        super.initIntro();
    }

    intro(): void {
        AppContext.clientHost.setLEDStripe(this.ledStripe, 2);

        super.intro();
    }

    outro(): void {
        RemoteManager.gamble("hidden");
        super.outro();
    }

    outroFinished(): void {
        super.outroFinished();
        GameManager.dontEnableGamePlay = false;
    }

    collect(): void {
        GameManager.dontEnableGamePlay = true;
        super.collect();
        DataManager.gameState("SecondaryGameCollectEscrow");
    }

    start(): void {
        if (!ModuleManager.primaryGameEnded()) {
            DataManager.gameState("PrimaryGameEnded");
        }
        super.start();
    }

    halve(): void {
        super.halve();
        DataManager.gameState("SecondaryGameHalved");
    }

    select(color: Communication.Game.Color): void {
        GameManager.dontEnableGamePlay = true;
        super.select(color);
        DataManager.gameState("SecondaryGameEscrow");
    }

    lose(): void {
        DataManager.gameState("SecondaryGameEnded");
        DataManager.gameState("GameEnded");
        super.lose();
    }

    startReceived(): void {
        super.startReceived();
        DataManager.gameState("SecondaryGameEntered");
    }

    halveReceived(): void {
        super.halveReceived();
        DataManager.gameState("SecondaryGameEntered");
    }

    selectReceived(): void {
        super.selectReceived();
        DataManager.gameState("SecondaryGameStarted");
        if (GameManager.gameResponse.totalWin > 0) {
            DataManager.gameState("SecondaryGameEntered");
        }
    }

    collectReceived(): void {
        DataManager.gameState("SecondaryGameEnded");
        DataManager.gameState("GameEnded");
        super.collectReceived();
    }

    limit(): void {
        super.limit();

        this.terminalActions.changeActionStates({
            [TerminalActions.StartWithCollect]: true,
            [TerminalActions.ActionLeft]: false,
            [TerminalActions.ActionRight]: false,
            [TerminalActions.BetOrGamble]: false,
        });

        AppContext.clientHost.actionsUpdated(this.getAllTerminalActions());
    }

    disable(): void {
        super.disable();

        this.terminalActions.disableAllActions();
        AppContext.clientHost.actionsUpdated(this.getAllTerminalActions());
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/Channels/FireBird/FireBirdGambleUserControls.ts
 **/